import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Shield, Eye, Heart } from "lucide-react";
import { SectionEyebrow, FadeInSection, PartnerStrip, FinalCTASection } from "../components/SharedSections";

const CHRIS_TEAM_PHOTO = "https://static.wixstatic.com/media/8cad63_91c959af9ad5415babec429e8fde7588~mv2.jpg/v1/fill/w_600,h_500,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Mayker%20Colour-20.jpg";
const KAI_PHOTO = "https://static.wixstatic.com/media/8cad63_9e375d858dde499baa3b54b9699fb3dc~mv2.jpg/v1/fill/w_600,h_500,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/8cad63_9e375d858dde499baa3b54b9699fb3dc~mv2.jpg";
const TEAM_GROUP_PHOTO = "https://static.wixstatic.com/media/8cad63_91c959af9ad5415babec429e8fde7588~mv2.jpg/v1/fill/w_800,h_600,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Mayker%20Colour-20.jpg";

const AboutPage = () => {
  useEffect(() => {
    document.title = "About Mayker Financial Group | Victoria BC";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", "Meet Chris Johnstone and Kai Simpson — independent financial advisors serving BC families and business owners since 2009.");
    }
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Independence",
      desc: "We're not tied to any single insurer or investment company. Our only loyalty is to our clients."
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: "Transparency",
      desc: "No hidden fees, no confusing jargon. We explain everything clearly so you can make informed decisions."
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Long-term relationships",
      desc: "We're not here for a single transaction. We're your advisors for life — through every stage and change."
    }
  ];

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="bg-[#0C1B33] pt-28 lg:pt-32 pb-16 lg:pb-20 px-4 lg:px-12 relative text-center">
        <div className="hero-bottom-line" />
        <div className="max-w-3xl mx-auto">
          <FadeInSection>
            <SectionEyebrow light centered>Our story</SectionEyebrow>
            <h1 className="font-playfair text-3xl lg:text-5xl font-bold text-white leading-tight mb-5">
              Built on trust. <em className="italic text-[#C9A84C]">Driven by relationships.</em>
            </h1>
            <p className="text-base lg:text-lg text-white/60 leading-relaxed">
              Mayker Financial Group was founded in Victoria in 2017 with one goal: to build the kind of financial advisory firm that actually puts clients first. Independent, local, and genuinely invested in your future.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* Origin Story */}
      <section className="bg-[#FAF8F3] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <FadeInSection>
              <img 
                src={TEAM_GROUP_PHOTO}
                alt="Mayker Financial Team"
                className="w-full rounded-lg shadow-[0_24px_60px_rgba(0,0,0,0.15)]"
              />
            </FadeInSection>

            <FadeInSection>
              <SectionEyebrow>Our beginning</SectionEyebrow>
              <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-[#0C1B33] leading-tight mb-5">
                Why we started Mayker
              </h2>
              <p className="text-[15px] text-[#6B7A99] leading-relaxed mb-4">
                Chris Johnstone founded Mayker Financial Group in 2017 — naming it after his daughters Lucy-May and Parker — after nearly a decade building one of Victoria's most respected financial practices.
              </p>
              <p className="text-[15px] text-[#6B7A99] leading-relaxed mb-4">
                The name reflects what's at the heart of everything we do: family, legacy, and building something that lasts.
              </p>
              <p className="text-[15px] text-[#6B7A99] leading-relaxed">
                Today, Mayker serves hundreds of families and business owners across British Columbia, with a team dedicated to honest, independent financial advice.
              </p>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* Meet the Team */}
      <section className="bg-[#F0EDE4] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <SectionEyebrow>Our team</SectionEyebrow>
            <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-[#0C1B33] leading-tight mb-10 lg:mb-12">
              Meet your advisors
            </h2>
          </FadeInSection>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Chris */}
            <FadeInSection>
              <div className="bg-white rounded-lg overflow-hidden border border-black/5 h-full">
                <img 
                  src={CHRIS_TEAM_PHOTO}
                  alt="Christopher Johnstone"
                  className="w-full h-72 object-cover object-top"
                />
                <div className="p-6 lg:p-8">
                  <h3 className="font-playfair text-2xl font-semibold text-[#0C1B33]">Christopher Johnstone</h3>
                  <p className="text-sm text-[#C9A84C] mb-4">President & Founder</p>
                  
                  <div className="text-sm text-[#6B7A99] leading-relaxed space-y-3">
                    <p>
                      Born and raised in Vancouver, British Columbia, Chris returned home in 2009 to establish his financial services practice after beginning his career in Alberta. A Queen's University Commerce graduate (Finance & International Business, 2005), Chris studied abroad in Sydney, Australia, and represented Queen's in baseball, badminton, and national mooting competitions.
                    </p>
                    <p>
                      Before entering financial services, Chris held roles in marketing and finance with Canadian Pacific Railway and Interpipeline Fund. Over the past 15+ years, Chris has built a successful financial advisory practice grounded in trust, integrity, and long-term relationships.
                    </p>
                    <p>
                      His leadership experience includes serving as Director of Business Development for Freedom 55 Financial in Victoria (2011–2017). In 2017, he founded Mayker Financial Group Inc., named after his daughters, Lucy-May and Parker.
                    </p>
                  </div>

                  <a 
                    href="https://www.linkedin.com/in/chris-johnstone-62764114/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-5 text-sm font-medium text-[#0C1B33] hover:text-[#C9A84C] transition-colors"
                  >
                    <Linkedin className="w-4 h-4" />
                    Connect on LinkedIn
                  </a>
                </div>
              </div>
            </FadeInSection>

            {/* Kai */}
            <FadeInSection>
              <div className="bg-white rounded-lg overflow-hidden border border-black/5 h-full">
                <img 
                  src={KAI_PHOTO}
                  alt="Kai Simpson"
                  className="w-full h-72 object-cover object-top"
                />
                <div className="p-6 lg:p-8">
                  <h3 className="font-playfair text-2xl font-semibold text-[#0C1B33]">Kai Simpson</h3>
                  <p className="text-sm text-[#C9A84C] mb-4">Financial Advisor</p>
                  
                  <div className="text-sm text-[#6B7A99] leading-relaxed space-y-3">
                    <p>
                      Born and raised in Victoria, BC, Kai is passionate about helping people plan for the future with clarity and confidence. As an advisor with Mayker, he focuses on creating personalized strategies that reflect each client's goals and values.
                    </p>
                    <p>
                      Outside of work, Kai loves travel, sports, music and spending time with family — they remind him why financial peace of mind matters.
                    </p>
                    <p>
                      His goal is to make the financial planning process simple, transparent and empowering for everyone he works with.
                    </p>
                  </div>

                  <a 
                    href="https://www.linkedin.com/in/kai-simpson-512b25114/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-5 text-sm font-medium text-[#0C1B33] hover:text-[#C9A84C] transition-colors"
                  >
                    <Linkedin className="w-4 h-4" />
                    Connect on LinkedIn
                  </a>
                </div>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="bg-[#0C1B33] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <SectionEyebrow light>Our values</SectionEyebrow>
            <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-white leading-tight mb-10 lg:mb-12">
              What we stand for
            </h2>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <FadeInSection key={index}>
                <div className="bg-white/5 border border-white/10 rounded-lg p-6 h-full">
                  <div className="w-12 h-12 bg-[#C9A84C]/20 rounded-lg flex items-center justify-center mb-4 text-[#C9A84C]">
                    {value.icon}
                  </div>
                  <h3 className="font-playfair text-lg font-semibold text-white mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed">
                    {value.desc}
                  </p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      <PartnerStrip />

      <FinalCTASection 
        headline="Let's build your financial future together."
        subheadline="Book a free call with Chris or Kai — no pressure, no jargon, just honest advice."
      />
    </div>
  );
};

export default AboutPage;
